import jsPDF from 'jspdf'
import 'jspdf-autotable'
import type { MainEngineConsumptionDto, MainEngineEntryDto } from '../core/_models'

const toMoney = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return '-'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

const formatValue = (value: number | null | undefined, unit?: string) => {
  if (value == null || Number.isNaN(value)) return '-'
  const formatted = Number(value).toFixed(1)
  return unit ? `${formatted} ${unit}` : formatted
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

export const generateMainEngineConsumptionPdf = (
  report: MainEngineConsumptionDto,
  vesselName: string
) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 40
  let currentY = 42

  // Title
  doc.setFontSize(18)
  doc.setTextColor(0, 0, 0)
  doc.text('Main Engine Consumption Report', marginX, currentY)
  currentY += 20

  // Header Information
  doc.setFontSize(10)
  doc.setTextColor(90, 90, 90)
  doc.text(`Vessel: ${vesselName}`, marginX, currentY)
  currentY += 14
  
  const monthName = report.reportMonth ? MONTHS[report.reportMonth - 1] : '-'
  doc.text(`Period: ${monthName} ${report.reportYear || '-'}`, marginX, currentY)
  currentY += 14
  
  doc.text(`Status: ${report.status || 'DRAFT'}`, marginX, currentY)
  currentY += 20

  // Basic Information Section
  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Basic Information', marginX, currentY)
  currentY += 5

  const basicInfoData = [
    ['Deadweight', report.deadweight || '-'],
    ['System Type', report.systemType || '-'],
    ['EPL Implementation Date', report.eplImplementationDate || '-'],
  ]

  ;(doc as any).autoTable({
    startY: currentY,
    head: [['Field', 'Value']],
    body: basicInfoData,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255] },
    bodyStyles: { textColor: [30, 30, 30] },
    margin: { left: marginX, right: marginX },
    theme: 'grid',
  })

  currentY = (doc as any).lastAutoTable?.finalY + 20 || currentY + 100

  // Engine Entries Section
  if (report.engineEntries && report.engineEntries.length > 0) {
    doc.setFontSize(12)
    doc.setTextColor(0, 0, 0)
    doc.text('Engine Details', marginX, currentY)
    currentY += 5

    report.engineEntries.forEach((engine: MainEngineEntryDto, index: number) => {
      const engineData = [
        ['Engine Make/Model', engine.engineMakeModel || '-'],
        ['Power (kW)', formatValue(engine.kw, 'kW')],
      ]

      ;(doc as any).autoTable({
        startY: currentY,
        head: [[`Engine #${index + 1}`, 'Value']],
        body: engineData,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [67, 97, 238], textColor: [255, 255, 255] },
        bodyStyles: { textColor: [30, 30, 30] },
        margin: { left: marginX, right: marginX },
        theme: 'grid',
      })

      currentY = (doc as any).lastAutoTable?.finalY + 5 || currentY + 80
    })

    currentY += 10
  }

  // Check if we need a new page
  if (currentY > doc.internal.pageSize.getHeight() - 200) {
    doc.addPage()
    currentY = 40
  }

  // Performance Data - Ballast Condition (12 knots)
  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Performance Data - Ballast Condition (12 knots)', marginX, currentY)
  currentY += 5

  const ballast12Data: any[] = []
  if (report.engineEntries && report.engineEntries.length > 0) {
    report.engineEntries.forEach((engine: MainEngineEntryDto, index: number) => {
      ballast12Data.push([
        `Engine #${index + 1}`,
        formatValue(engine.ballastSpeed12, 'kn'),
        formatValue(engine.ballastCons12, 't/day'),
        formatValue(engine.ballastRpm12, 'RPM'),
      ])
    })
  }

  if (ballast12Data.length > 0) {
    ;(doc as any).autoTable({
      startY: currentY,
      head: [['Engine', 'Speed', 'Consumption', 'RPM']],
      body: ballast12Data,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255] },
      bodyStyles: { textColor: [30, 30, 30] },
      margin: { left: marginX, right: marginX },
      theme: 'grid',
    })

    currentY = (doc as any).lastAutoTable?.finalY + 15 || currentY + 80
  }

  // Performance Data - Laden Condition (12 knots)
  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Performance Data - Laden Condition (12 knots)', marginX, currentY)
  currentY += 5

  const laden12Data: any[] = []
  if (report.engineEntries && report.engineEntries.length > 0) {
    report.engineEntries.forEach((engine: MainEngineEntryDto, index: number) => {
      laden12Data.push([
        `Engine #${index + 1}`,
        formatValue(engine.ladenSpeed12, 'kn'),
        formatValue(engine.ladenCons12, 't/day'),
        formatValue(engine.ladenRpm12, 'RPM'),
      ])
    })
  }

  if (laden12Data.length > 0) {
    ;(doc as any).autoTable({
      startY: currentY,
      head: [['Engine', 'Speed', 'Consumption', 'RPM']],
      body: laden12Data,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [50, 150, 89], textColor: [255, 255, 255] },
      bodyStyles: { textColor: [30, 30, 30] },
      margin: { left: marginX, right: marginX },
      theme: 'grid',
    })

    currentY = (doc as any).lastAutoTable?.finalY + 15 || currentY + 80
  }

  // Check if we need a new page
  if (currentY > doc.internal.pageSize.getHeight() - 200) {
    doc.addPage()
    currentY = 40
  }

  // Performance Data - Eco Speed
  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Performance Data - Eco Speed', marginX, currentY)
  currentY += 5

  const ecoData: any[] = []
  if (report.engineEntries && report.engineEntries.length > 0) {
    report.engineEntries.forEach((engine: MainEngineEntryDto, index: number) => {
      ecoData.push([
        `Engine #${index + 1} (Ballast)`,
        formatValue(engine.ballastSpeedEco, 'kn'),
        formatValue(engine.ballastConsEco, 't/day'),
        formatValue(engine.ballastRpmEco, 'RPM'),
      ])
      ecoData.push([
        `Engine #${index + 1} (Laden)`,
        formatValue(engine.ladenSpeedEco, 'kn'),
        formatValue(engine.ladenConsEco, 't/day'),
        formatValue(engine.ladenRpmEco, 'RPM'),
      ])
    })
  }

  if (ecoData.length > 0) {
    ;(doc as any).autoTable({
      startY: currentY,
      head: [['Engine/Condition', 'Speed', 'Consumption', 'RPM']],
      body: ecoData,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [115, 57, 234], textColor: [255, 255, 255] },
      bodyStyles: { textColor: [30, 30, 30] },
      margin: { left: marginX, right: marginX },
      theme: 'grid',
    })

    currentY = (doc as any).lastAutoTable?.finalY + 15 || currentY + 80
  }

  // Performance Data - 83% MCR
  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Performance Data - 83% MCR', marginX, currentY)
  currentY += 5

  const mcr83Data = [
    ['Ballast Speed', formatValue(report.ballastSpeed83, 'kn')],
    ['Ballast Consumption', formatValue(report.ballastCons83, 't/day')],
    ['Ballast RPM', formatValue(report.ballastRpm83, 'RPM')],
    ['Laden Speed', formatValue(report.ladenSpeed83, 'kn')],
    ['Laden Consumption', formatValue(report.ladenCons83, 't/day')],
    ['Laden RPM', formatValue(report.ladenRpm83, 'RPM')],
  ]

  ;(doc as any).autoTable({
    startY: currentY,
    head: [['Parameter', 'Value']],
    body: mcr83Data,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [204, 83, 44], textColor: [255, 255, 255] },
    bodyStyles: { textColor: [30, 30, 30] },
    margin: { left: marginX, right: marginX },
    theme: 'grid',
  })

  currentY = (doc as any).lastAutoTable?.finalY + 15 || currentY + 100

  // Additional Technical Data
  if (currentY > doc.internal.pageSize.getHeight() - 200) {
    doc.addPage()
    currentY = 40
  }

  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Additional Technical Data', marginX, currentY)
  currentY += 5

  const technicalData = [
    ['Max Power EPL MCR Limit', formatValue(report.maxPowerEplMcrLim, 'kW')],
    ['Max Consumption Ref EEXI', formatValue(report.maxConsumptionRefEexi, 't/day')],
    ['Design SFOC from EEXI', formatValue(report.designSfocFromEexi, 'g/kWh')],
    ['Actual SFOC 83%', formatValue(report.actualSfoc83, 'g/kWh')],
    ['New Attainable Power', formatValue(report.newAttainablePower, 'kW')],
    ['New Speed Vref', formatValue(report.newSpeedVref, 'kn')],
  ]

  ;(doc as any).autoTable({
    startY: currentY,
    head: [['Parameter', 'Value']],
    body: technicalData,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255] },
    bodyStyles: { textColor: [30, 30, 30] },
    margin: { left: marginX, right: marginX },
    theme: 'grid',
  })

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 30
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text(
    `Generated on ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}`,
    marginX,
    footerY
  )

  // Generate filename
  const safeVesselName = vesselName.replace(/[^a-z0-9_-]+/gi, '_')
  const monthStr = String(report.reportMonth || 1).padStart(2, '0')
  const fileName = `${safeVesselName}_main_engine_consumption_${report.reportYear}_${monthStr}.pdf`

  // Save the PDF
  doc.save(fileName)
}
